import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaChild, FaCheckCircle, FaTimes, FaSpinner, FaClock,
  FaEye, FaThumbsUp, FaThumbsDown, FaUserCircle, FaGraduationCap,
  FaFileMedicalAlt
} from 'react-icons/fa';
import ChildFutureTestModal from './ChildFutureTestModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingChildFutureTests = () => {
  const [loading, setLoading] = useState(true);
  const [pendingTests, setPendingTests] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { clientId, clientName }

  useEffect(() => {
    fetchPendingTests();
  }, []);

  const fetchPendingTests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/child-future-test/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingTests(response.data || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (testId) => {
    setProcessingId(testId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/child-future-test/${testId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPendingTests(); // Refresh list
      setSelectedTest(null);
    } catch (err) {
      alert('Failed to approve test. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedTest) return;
    setProcessingId(selectedTest.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/child-future-test/${selectedTest.id}/reject`, {
        reason: rejectReason || 'Please resubmit with accurate information.'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPendingTests();
      setSelectedTest(null);
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err) {
      alert('Failed to reject test. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const questions = [
    "Have you had a career planning conversation with your child?",
    "Do you know your child's interests, skills, & personality strengths?",
    "Have you set a career goal or direction for your child based on future demand?",
    "How confident are you in funding your child's higher education?",
    "Do you have a dedicated financial plan or education fund for your child?",
    "Are you aware of the rising costs of Indian & international education?",
    "Have you considered psychometric career tests or career counseling?",
    "Do you track or review your investments related to your child's goals?",
    "Do you want your child to follow a planned career path or decide on their own?",
    "Would you like a free consultation on career & financial readiness for your child?"
  ];

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-center h-20">
          <FaSpinner className="animate-spin text-[#FFD700] text-xl" />
        </div>
      </div>
    );
  }

  if (pendingTests.length === 0) {
    return null; // Don't show section if no pending tests
  }

  return (
    <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-orange-500/30">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center">
          <FaChild className="text-orange-400 mr-2" />
          Pending Child Future Tests
          <span className="ml-2 px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full text-sm">
            {pendingTests.length}
          </span>
        </h3>
      </div>

      <div className="space-y-3">
        {pendingTests.map((test) => (
          <div 
            key={test.id}
            className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F] hover:border-orange-500/50 transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                  <FaChild className="text-orange-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{test.child_name}</p>
                  <p className="text-gray-400 text-sm">
                    Age: {test.child_age} | {test.child_class} | Client: {test.client_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-[#FFD700] font-bold">{test.total_score}/40</span>
                <button
                  onClick={() => setDetailModal({ clientId: test.client_id, clientName: test.client_name })}
                  className="px-3 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40 rounded-lg text-sm transition-all"
                  data-testid={`view-cft-details-${test.id}`}
                >
                  <FaFileMedicalAlt className="inline mr-1" />
                  View Details
                </button>
                <button
                  onClick={() => setSelectedTest(selectedTest?.id === test.id ? null : test)}
                  className="px-3 py-1 bg-[#1E3A5F] hover:bg-[#2D5A8F] text-white rounded-lg text-sm transition-all"
                >
                  <FaEye className="inline mr-1" />
                  {selectedTest?.id === test.id ? 'Hide' : 'Review'}
                </button>
              </div>
            </div>

            {/* Expanded View */}
            {selectedTest?.id === test.id && (
              <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
                {/* Answers Summary */}
                <div className="mb-4">
                  <p className="text-gray-400 text-sm mb-2">Responses:</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {test.answers.map((ans, idx) => (
                      <div key={idx} className="flex items-start space-x-2 text-sm">
                        <span className="text-[#FFD700] font-medium min-w-[24px]">Q{ans.question_number}:</span>
                        <span className="text-gray-300 flex-1">{questions[ans.question_number - 1]}</span>
                        <span className={`font-bold ${
                          ans.answer_value === 4 ? 'text-green-400' :
                          ans.answer_value === 3 ? 'text-blue-400' :
                          ans.answer_value === 2 ? 'text-orange-400' : 'text-red-400'
                        }`}>{ans.answer_value}/4</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Score Interpretation Preview */}
                <div className={`p-3 rounded-lg mb-4 ${
                  test.total_score >= 35 ? 'bg-green-500/10 border border-green-500/30' :
                  test.total_score >= 26 ? 'bg-blue-500/10 border border-blue-500/30' :
                  test.total_score >= 15 ? 'bg-orange-500/10 border border-orange-500/30' :
                  'bg-red-500/10 border border-red-500/30'
                }`}>
                  <p className={`text-sm font-medium ${
                    test.total_score >= 35 ? 'text-green-400' :
                    test.total_score >= 26 ? 'text-blue-400' :
                    test.total_score >= 15 ? 'text-orange-400' : 'text-red-400'
                  }`}>
                    {test.total_score >= 35 ? 'Proactive Planner' :
                     test.total_score >= 26 ? 'Aware but Needs Action' :
                     test.total_score >= 15 ? 'At Risk — Act Now' : 'Unprepared but Not Too Late'}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Recommendation: {test.child_age <= 11 ? 'Shiksha Sankalp' : 'Goal Manthan'}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleApprove(test.id)}
                    disabled={processingId === test.id}
                    className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center justify-center transition-all disabled:opacity-50"
                  >
                    {processingId === test.id ? (
                      <FaSpinner className="animate-spin mr-2" />
                    ) : (
                      <FaThumbsUp className="mr-2" />
                    )}
                    Approve & Send Results
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={processingId === test.id}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-medium flex items-center transition-all disabled:opacity-50"
                  >
                    <FaThumbsDown className="mr-2" />
                    Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Full Details Modal (client's Child Future Ready Test — full history) */}
      {detailModal && (
        <ChildFutureTestModal
          clientId={detailModal.clientId}
          clientName={detailModal.clientName}
          onClose={() => setDetailModal(null)}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedTest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowRejectModal(false)}>
          <div className="bg-[#1A2942] rounded-xl p-6 max-w-md w-full mx-4 border border-[#2D5A8F]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-4">Reject Test Submission</h3>
            <p className="text-gray-400 text-sm mb-4">
              Rejecting test for {selectedTest.child_name}. Please provide a reason:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-4 py-3 text-white focus:border-red-500 focus:outline-none resize-none"
              rows={3}
            />
            <div className="flex items-center space-x-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-2 bg-[#0F172A] hover:bg-[#2D5A8F] text-white rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processingId}
                className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium flex items-center justify-center transition-all disabled:opacity-50"
              >
                {processingId ? <FaSpinner className="animate-spin mr-2" /> : <FaThumbsDown className="mr-2" />}
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingChildFutureTests;
